// OAuth2 PKCE flow utilities

/**
 * Generate a random string for the code verifier
 * @param length Length of the code verifier
 * @returns Random string
 */
export const generateCodeVerifier = (length: number = 64): string => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const values = new Uint8Array(length);
  crypto.getRandomValues(values);
  
  for (let i = 0; i < length; i++) {
    result += charset[values[i] % charset.length];
  }
  
  return result;
};

/**
 * Generate a code challenge from the code verifier using SHA-256
 * @param codeVerifier The code verifier
 * @returns Base64URL encoded code challenge
 */
export const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  // Hash the code verifier using SHA-256
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  
  // Convert the hash to base64url
  return base64UrlEncode(hash);
};

/**
 * Encode an ArrayBuffer to base64url
 * @param buffer ArrayBuffer to encode
 * @returns Base64URL encoded string
 */
export const base64UrlEncode = (buffer: ArrayBuffer): string => {
  // Convert the buffer to base64
  const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
  
  // Convert base64 to base64url by replacing characters and removing padding
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

/**
 * Parse URL parameters from a string
 * @param search URL search string
 * @returns Object with parsed parameters
 */
export const parseUrlParams = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};
  
  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  
  return result;
};

interface OAuthState {
  returnUrl: string;
  workspaceId: string;
}

/**
 * Store OAuth state in session storage
 * @param state State object to store
 * @param stateParam State parameter used in the OAuth flow
 */
export const storeOAuthState = (state: OAuthState, stateParam: string): void => {
  sessionStorage.setItem(`oauth_state_${stateParam}`, JSON.stringify(state));
};

/**
 * Get OAuth state from session storage
 * @param stateParam State parameter used in the OAuth flow
 * @returns Stored state object or null if not found
 */
export const getOAuthState = (stateParam: string): OAuthState | null => {
  const state = sessionStorage.getItem(`oauth_state_${stateParam}`);
  if (!state) return null;
  
  try {
    return JSON.parse(state);
  } catch (e) {
    console.error('Failed to parse OAuth state', e);
    return null;
  }
};

/**
 * Clear OAuth state from session storage
 * @param stateParam State parameter used in the OAuth flow
 */
export const clearOAuthState = (stateParam: string): void => {
  sessionStorage.removeItem(`oauth_state_${stateParam}`);
};

/**
 * Store code verifier in session storage
 * @param codeVerifier Code verifier to store
 * @param stateParam State parameter used in the OAuth flow
 */
export const storeCodeVerifier = (codeVerifier: string, stateParam: string): void => {
  sessionStorage.setItem(`code_verifier_${stateParam}`, codeVerifier);
};

/**
 * Get code verifier from session storage
 * @param stateParam State parameter used in the OAuth flow
 * @returns Stored code verifier or null if not found
 */
export const getCodeVerifier = (stateParam: string): string | null => {
  return sessionStorage.getItem(`code_verifier_${stateParam}`);
};

/**
 * Clear code verifier from session storage
 * @param stateParam State parameter used in the OAuth flow
 */
export const clearCodeVerifier = (stateParam: string): void => {
  sessionStorage.removeItem(`code_verifier_${stateParam}`);
};

/**
 * Create a credential ID for OAuth tokens
 * @param issuer The token issuer (e.g., 'github', 'gitlab')
 * @returns Credential ID
 */
export const createOAuthCredentialId = (issuer: string): string => {
  return `oauth_${issuer}_${Date.now()}`;
};
